import {
    AlertTriangle,
    ArrowRight,
    BadgeIndianRupee,
    CalendarClock,
    ChevronDown,
    CheckCircle2,
    ChevronLeft,
    CircleAlert,
    CircleDollarSign,
    Download,
    ExternalLink,
    FileWarning,
    LoaderCircle,
    PencilLine,
    RefreshCw,
    Send,
    Share2,
    ShieldAlert,
    TrendingDown,
    TrendingUp,
    Users,
    WalletCards,
    X,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FaArrowRightLong, FaCircleUser } from 'react-icons/fa6'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './PayrollRunDetailScreen.module.css'

const TAB_KEYS = {
    SUMMARY: 'SUMMARY',
    STAFF: 'STAFF',
    EXCLUDED: 'EXCLUDED',
    WARNINGS: 'WARNINGS',
}

const RUN_STATUS_CONFIG = {
    DRAFT: { label: 'Draft', color: '#E65100', bg: '#FFF3E0' },
    REVIEW: { label: 'In Review', color: '#1565C0', bg: '#E3F2FD' },
    FINALIZED: { label: 'Finalized', color: '#2E7D32', bg: '#E8F5E9' },
}

const EXCLUSION_STATUS_CONFIG = {
    FNF_SETTLED: { label: 'FnF Settled', color: '#6A1B9A', bg: '#F3E5F5' },
    NO_ATTENDANCE_DATA: { label: 'No Attendance', color: '#C62828', bg: '#FFEBEE' },
    NO_SALARY_ASSIGNMENT: { label: 'No Salary', color: '#E65100', bg: '#FFF3E0' },
    MANUALLY_EXCLUDED: { label: 'Excluded', color: '#546E7A', bg: '#ECEFF1' },
}

const EXPORT_OPTIONS = [
    { format: 'FULL', label: 'Full Export', desc: 'All sheets', icon: Download },
    { format: 'SALARY_SUMMARY', label: 'Salary Summary', desc: 'Staff salary sheet only', icon: BadgeIndianRupee },
    { format: 'COMPLIANCE', label: 'PF / ESI Compliance', desc: 'Statutory deductions', icon: ShieldAlert },
    { format: 'ADVANCES', label: 'Advance Deductions', desc: 'EMI deductions sheet', icon: WalletCards },
]

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatINR = (amount) => {
    const value = Number(amount)
    if (!Number.isFinite(value)) return '—'
    return value.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })
}

const formatSignedINR = (amount) => {
    const value = Number(amount || 0)
    if (value === 0) return '₹0'
    return `${value > 0 ? '+' : '−'}₹${formatINR(Math.abs(value))}`
}

const formatDateTime = (value) => {
    if (!value) return '—'
    const parsed = String(value).includes(' ') ? String(value).replace(' ', 'T') : String(value)
    const date = new Date(parsed)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const sheetToCSV = (sheet) => {
    const escapeCell = (val) => {
        if (val === null || val === undefined) return ''
        const cell = String(val)
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
            return `"${cell.replace(/"/g, '""')}"`
        }
        return cell
    }

    const lines = []
    const headers = Array.isArray(sheet?.headers) ? sheet.headers : []
    const rows = Array.isArray(sheet?.rows) ? sheet.rows : []

    if (headers.length > 0) {
        lines.push(headers.map(escapeCell).join(','))
    }

    rows.forEach((row) => {
        const safeRow = Array.isArray(row) ? row : []
        lines.push(safeRow.map(escapeCell).join(','))
    })

    return lines.join('\n')
}

const downloadAsCSV = (sheets, filename) => {
    const safeSheets = Array.isArray(sheets) ? sheets : []
    if (safeSheets.length === 0) {
        toast.error('No export data available for download.')
        return
    }

    const parts = []
    safeSheets.forEach((sheet) => {
        parts.push(`## ${sheet?.sheetName || 'Sheet'}`)
        parts.push(sheetToCSV(sheet))
        parts.push('')
    })

    const content = '\uFEFF' +  parts.join('\n')
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.setAttribute('href', url)
    link.setAttribute('download', filename || `payroll_export_${Date.now()}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

const PayrollRunDetailScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { runIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [runData, setRunData] = useState(() => {
        const seeded = location?.state?.latestRun
        if (!seeded) return null
        if (seeded?.runIdentifier && runIdentifier && seeded.runIdentifier !== runIdentifier) return null
        return seeded
    })
    const [isLoading, setIsLoading] = useState(true)
    const [activeTab, setActiveTab] = useState(TAB_KEYS.SUMMARY)
    const [isRecomputing, setIsRecomputing] = useState(false)
    const [requiresAck, setRequiresAck] = useState(false)
    const [ackWarnings, setAckWarnings] = useState([])
    const [ackCheckboxChecked, setAckCheckboxChecked] = useState(false)
    const [diff, setDiff] = useState(null)
    const [recomputingStaffIdentifier, setRecomputingStaffIdentifier] = useState('')
    const [staffAckContext, setStaffAckContext] = useState(null)
    const [staffAckChecked, setStaffAckChecked] = useState(false)
    const [staffRecomputeDiffMap, setStaffRecomputeDiffMap] = useState({})
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [confirmationToken, setConfirmationToken] = useState(null)
    const [finalizationSummary, setFinalizationSummary] = useState(null)
    const [finalizationMessage, setFinalizationMessage] = useState('')
    const [confirmationTokenExpiresIn, setConfirmationTokenExpiresIn] = useState(0)
    const [showFinalizeModal, setShowFinalizeModal] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const [isExportMenuOpen, setIsExportMenuOpen] = useState(false)
    const [isGeneratingShareLinks, setIsGeneratingShareLinks] = useState(false)
    const [isSendingPayslips, setIsSendingPayslips] = useState(false)
    const exportMenuRef = useRef(null)

    const fetchRunDetail = useCallback(async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) return
        if (!runIdentifier) {
            toast.error('Payroll run context is missing.')
            navigate('/payroll/run-history')
            return
        }

        try {
            setIsLoading(true)

            const response = await fetch(BASE_URL_API + 'getPayrollRunDetail', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    runIdentifier,
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                setRunData(respo.response)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            toast.error(respo?.message || 'Failed to load payroll run.')
            navigate('/payroll/setup')
        } catch {
            toast.error('Network error.')
        } finally {
            setIsLoading(false)
        }
    }, [activeStore?.storeIdentifier, authCtx, navigate, runIdentifier])

    useEffect(() => {
        fetchRunDetail()
    }, [fetchRunDetail])

    useEffect(() => {
        const onFocus = () => {
            fetchRunDetail()
        }

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') onFocus()
        }

        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibilityChange)

        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
    }, [fetchRunDetail])

    useEffect(() => {
        if (!isExportMenuOpen) return

        const handleOutside = (event) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
                setIsExportMenuOpen(false)
            }
        }

        const handleEscape = (event) => {
            if (event.key === 'Escape') setIsExportMenuOpen(false)
        }

        document.addEventListener('mousedown', handleOutside)
        document.addEventListener('keydown', handleEscape)

        return () => {
            document.removeEventListener('mousedown', handleOutside)
            document.removeEventListener('keydown', handleEscape)
        }
    }, [isExportMenuOpen])

    const totals = runData?.totals || {}
    const staffSummaries = runData?.staffSummaries || []
    const excludedStaff = runData?.excludedStaff || []
    const warnings = runData?.warnings || []
    const warningCount = Number(runData?.warningCount ?? warnings.length)
    const canFinalize = Boolean(runData?.canFinalize)
    const finalizationGates = runData?.finalizationGates || []
    const runStatusKey = String(runData?.runStatus || 'DRAFT').toUpperCase()
    const statusMeta = RUN_STATUS_CONFIG[runStatusKey] || {
        label: runStatusKey,
        color: '#334155',
        bg: '#E2E8F0',
    }

    const tabCounts = useMemo(
        () => ({
            [TAB_KEYS.SUMMARY]: null,
            [TAB_KEYS.STAFF]: staffSummaries.length,
            [TAB_KEYS.EXCLUDED]: excludedStaff.length,
            [TAB_KEYS.WARNINGS]: warningCount,
        }),
        [excludedStaff.length, staffSummaries.length, warningCount]
    )

    const handleStaffRowPress = (summary) => {
        if (!summary?.staffIdentifier) return
        navigate(`/payroll/run/${runIdentifier}/payslip/${summary.staffIdentifier}`)
    }

    const callTransitionAPI = useCallback(
        async (targetStatus, token = null) => {
            if (!authCtx?.token || !activeStore?.storeIdentifier || !runIdentifier) return null

            const body = {
                token: authCtx.token,
                storeIdentifier: activeStore.storeIdentifier,
                runIdentifier,
                targetStatus,
                confirmedBy: authCtx?.authIdentifier,
            }

            if (token) body.confirmationToken = token

            const response = await fetch(BASE_URL_API + 'transitionPayrollRunStatus', {
                method: 'POST',
                body: JSON.stringify(body),
            })

            return response.json()
        },
        [activeStore?.storeIdentifier, authCtx, runIdentifier]
    )

    const resetFinalizationModal = useCallback(() => {
        setShowFinalizeModal(false)
        setConfirmationToken(null)
        setFinalizationSummary(null)
        setFinalizationMessage('')
        setConfirmationTokenExpiresIn(0)
    }, [])

    const handleTransitionError = useCallback(
        (respo) => {
            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'PAY_007':
                    toast.error(respo?.message || 'Cannot finalize due to pending attendance.')
                    break
                case 'ADV_004':
                    toast.error('Invalid confirmation. Try again.')
                    resetFinalizationModal()
                    break
                case 'PAY_002':
                case 'PAY_001':
                case 'VAL_001':
                case 'VAL_003':
                case 'SYS_002':
                default:
                    toast.error(respo?.message || 'Action failed.')
            }
        },
        [authCtx, resetFinalizationModal]
    )

    const handleMoveToReview = useCallback(async () => {
        try {
            setIsTransitioning(true)
            const respo = await callTransitionAPI('REVIEW')
            if (respo?.status === 'success') {
                toast.success('Payroll moved to Review.')
                fetchRunDetail()
                return
            }
            handleTransitionError(respo)
        } catch {
            toast.error('Network error.')
        } finally {
            setIsTransitioning(false)
        }
    }, [callTransitionAPI, fetchRunDetail, handleTransitionError])

    const handleMoveToDraft = useCallback(async () => {
        try {
            setIsTransitioning(true)
            const respo = await callTransitionAPI('DRAFT')
            if (respo?.status === 'success') {
                toast.info('Payroll moved back to Draft.')
                fetchRunDetail()
                return
            }
            handleTransitionError(respo)
        } catch {
            toast.error('Network error.')
        } finally {
            setIsTransitioning(false)
        }
    }, [callTransitionAPI, fetchRunDetail, handleTransitionError])

    const requestFinalizationToken = useCallback(async () => {
        try {
            setIsTransitioning(true)
            const respo = await callTransitionAPI('FINALIZED')

            if (respo?.errorCode === 'PAY_009' && respo?.response?.requiresConfirmation) {
                setConfirmationToken(respo?.response?.confirmationToken || null)
                setFinalizationSummary(respo?.response?.finalizationSummary || null)
                setConfirmationTokenExpiresIn(Number(respo?.response?.confirmationTokenExpiresIn || 0))
                setFinalizationMessage(respo?.response?.message || respo?.message || '')
                setShowFinalizeModal(true)
                return
            }

            if (respo?.status === 'success') {
                toast.success(respo?.response?.message || respo?.message || 'Payroll finalized.')
                resetFinalizationModal()
                fetchRunDetail()
                return
            }

            handleTransitionError(respo)
        } catch {
            toast.error('Network error.')
        } finally {
            setIsTransitioning(false)
        }
    }, [callTransitionAPI, fetchRunDetail, handleTransitionError, resetFinalizationModal])

    const confirmFinalization = useCallback(async () => {
        if (!confirmationToken) {
            requestFinalizationToken()
            return
        }

        try {
            setIsTransitioning(true)
            const respo = await callTransitionAPI('FINALIZED', confirmationToken)

            if (respo?.status === 'success') {
                toast.success(respo?.response?.message || respo?.message || 'Payroll finalized.')
                resetFinalizationModal()
                fetchRunDetail()
                return
            }

            if (respo?.errorCode === 'ADV_005') {
                toast.warning('Confirmation expired. Fetching a fresh confirmation.')
                const retryRespo = await callTransitionAPI('FINALIZED')
                if (retryRespo?.errorCode === 'PAY_009' && retryRespo?.response?.requiresConfirmation) {
                    setConfirmationToken(retryRespo?.response?.confirmationToken || null)
                    setFinalizationSummary(retryRespo?.response?.finalizationSummary || null)
                    setConfirmationTokenExpiresIn(Number(retryRespo?.response?.confirmationTokenExpiresIn || 0))
                    setFinalizationMessage(retryRespo?.response?.message || retryRespo?.message || '')
                    setShowFinalizeModal(true)
                    return
                }
                handleTransitionError(retryRespo)
                return
            }

            handleTransitionError(respo)
        } catch {
            toast.error('Network error.')
        } finally {
            setIsTransitioning(false)
        }
    }, [callTransitionAPI, confirmationToken, fetchRunDetail, handleTransitionError, requestFinalizationToken, resetFinalizationModal])

    const handleSendPayslips = useCallback(async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier || !runIdentifier) {
            toast.error('Missing payroll context for WhatsApp delivery.')
            return
        }

        if (!Array.isArray(staffSummaries) || staffSummaries.length === 0) {
            toast.info('No staff rows available for payslip delivery.')
            return
        }

        const results = { sent: 0, failed: 0, noPhone: 0 }

        try {
            setIsSendingPayslips(true)

            for (const staff of staffSummaries) {
                if (!staff?.staffIdentifier) {
                    results.failed += 1
                    continue
                }

                try {
                    const response = await fetch(BASE_URL_API + 'sendPayslipOnWhatsapp', {
                        method: 'POST',
                        body: JSON.stringify({
                            token: authCtx.token,
                            storeIdentifier: activeStore.storeIdentifier,
                            runIdentifier,
                            staffIdentifier: staff.staffIdentifier,
                        }),
                    })

                    const respo = await response.json()

                    if (respo?.message === 'tokenExpired') {
                        authCtx.logout()
                        return
                    }

                    if (respo?.status === 'success') {
                        results.sent += 1
                    } else if (respo?.errorCode === 'PAY_005') {
                        results.noPhone += 1
                    } else {
                        results.failed += 1
                    }
                } catch {
                    results.failed += 1
                }

                await new Promise((resolve) => setTimeout(resolve, 300))
            }

            const message = `Payslips sent: ${results.sent} delivered, ${results.failed} failed, ${results.noPhone} no phone number.`
            if (results.failed > 0 || results.noPhone > 0) {
                toast.warn(message)
            } else {
                toast.success(message)
            }
            navigate(`/payroll/run/${runIdentifier}/delivery-status`)
        } catch {
            toast.error('Network error.')
        } finally {
            setIsSendingPayslips(false)
        }
    }, [activeStore?.storeIdentifier, authCtx, navigate, runIdentifier, staffSummaries])

    const handleGenerateAllShareLinks = useCallback(async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier || !runIdentifier) {
            toast.error('Missing payroll context for share links.')
            return
        }

        if (!Array.isArray(staffSummaries) || staffSummaries.length === 0) {
            toast.info('No staff rows available for token generation.')
            return
        }

        try {
            setIsGeneratingShareLinks(true)

            const responses = await Promise.all(
                staffSummaries.map(async (staff) => {
                    if (!staff?.staffIdentifier) {
                        return { status: 'error', errorCode: 'VAL_001', message: 'Missing staff identifier.' }
                    }

                    try {
                        const response = await fetch(BASE_URL_API + 'generatePayslipShareToken', {
                            method: 'POST',
                            body: JSON.stringify({
                                token: authCtx.token,
                                storeIdentifier: activeStore.storeIdentifier,
                                runIdentifier,
                                staffIdentifier: staff.staffIdentifier,
                            }),
                        })

                        return response.json()
                    } catch {
                        return { status: 'error', errorCode: 'NETWORK_ERROR', message: 'Network error.' }
                    }
                })
            )

            if (responses.some((item) => item?.message === 'tokenExpired')) {
                authCtx.logout()
                return
            }

            let successCount = 0
            let existingCount = 0
            let failedCount = 0

            responses.forEach((respo) => {
                if (respo?.status === 'success') {
                    successCount += 1
                    if (respo?.response?.isExisting) existingCount += 1
                    return
                }
                failedCount += 1
            })

            if (successCount > 0) {
                toast.success(
                    `Share links ready for ${successCount} staff${existingCount > 0 ? ` (${existingCount} existing)` : ''}.`
                )
            }

            if (failedCount > 0) {
                toast.warn(`${failedCount} staff link(s) failed. Please retry.`)
            }
        } catch {
            toast.error('Network error.')
        } finally {
            setIsGeneratingShareLinks(false)
        }
    }, [activeStore?.storeIdentifier, authCtx, runIdentifier, staffSummaries])

    const handleExport = useCallback(
        async (format = 'FULL') => {
            if (!authCtx?.token || !activeStore?.storeIdentifier || !runIdentifier) {
                toast.error('Missing payroll context for export.')
                return
            }

            try {
                setIsExporting(true)
                setIsExportMenuOpen(false)

                const response = await fetch(BASE_URL_API + 'exportPayrollRun', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier: activeStore.storeIdentifier,
                        runIdentifier,
                        exportFormat: format,
                    }),
                })

                const respo = await response.json()

                if (respo?.status === 'success') {
                    const sheets = respo?.response?.sheets || []
                    const suggestedFilename = respo?.response?.suggestedFilename || `payroll_export_${runIdentifier}.csv`
                    downloadAsCSV(sheets, suggestedFilename)
                    toast.success(respo?.message || 'Export ready.')
                    return
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                switch (respo?.errorCode) {
                    case 'PAY_001':
                    case 'VAL_001':
                        toast.error(respo?.message || 'Unable to export payroll run.')
                        break
                    default:
                        toast.error(respo?.message || 'Export failed.')
                }
            } catch {
                toast.error('Network error.')
            } finally {
                setIsExporting(false)
            }
        },
        [activeStore?.storeIdentifier, authCtx, runIdentifier]
    )

    const exportButtonLabel = runStatusKey === 'FINALIZED' ? 'Export' : 'Export Preview'

    const openAttendanceScreen = (warning) => {
        navigate(`/staff/${warning?.staffIdentifier}/attendance`, {
            state: {
                staffIdentifier: warning?.staffIdentifier,
                fromPayrollRun: true,
                runIdentifier,
                cycleYear: runData?.cycleYear,
                cycleMonth: runData?.cycleMonth,
            },
        })
    }

    const recomputePayroll = useCallback(
        async (acknowledgeWarnings = false) => {
            if (!authCtx?.token || !activeStore?.storeIdentifier) return

            try {
                setIsRecomputing(true)

                const response = await fetch(BASE_URL_API + 'recomputePayrollRun', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier: activeStore.storeIdentifier,
                        runIdentifier,
                        acknowledgeWarnings,
                    }),
                })

                const respo = await response.json()

                if (respo?.status === 'success') {
                    setDiff(respo.response.diff)
                    setRequiresAck(false)
                    setAckWarnings([])
                    setAckCheckboxChecked(false)

                    fetchRunDetail()

                    const changedCount = respo.response.diff?.changedCount || 0
                    const message =
                        changedCount > 0
                            ? `Payroll recomputed. ${changedCount} staff value(s) changed.`
                            : 'Payroll recomputed. No changes detected.'
                    toast.success(message)
                    return
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                switch (respo?.errorCode) {
                    case 'PAY_003':
                    case 'PAY_006':
                        setAckWarnings(respo.response?.warnings || [])
                        setRequiresAck(true)
                        break
                    case 'PAY_002':
                        toast.error(
                            `Cannot recompute — run is ${respo.response?.currentStatus || 'in an invalid state'}.`
                        )
                        break
                    default:
                        toast.error(respo?.message || 'Recompute failed.')
                }
            } catch (error) {
                toast.error('Network error.')
            } finally {
                setIsRecomputing(false)
            }
        },
        [authCtx, activeStore?.storeIdentifier, runIdentifier, fetchRunDetail]
    )

    const applyStaffRecomputeDelta = useCallback((responsePayload) => {
        if (!responsePayload?.staffIdentifier) return

        setRunData((prev) => {
            if (!prev) return prev

            const staffIdentifier = responsePayload.staffIdentifier
            const updatedSummaries = (prev.staffSummaries || []).map((summary) => {
                if (summary?.staffIdentifier !== staffIdentifier) return summary

                return {
                    ...summary,
                    summaryIdentifier: responsePayload.newSummaryIdentifier || summary.summaryIdentifier,
                    netPay: Number(responsePayload.newNetPay ?? summary.netPay ?? 0),
                    isBelowMinimumWage:
                        typeof responsePayload.isBelowMinimumWage === 'boolean'
                            ? responsePayload.isBelowMinimumWage
                            : summary.isBelowMinimumWage,
                    earnings: {
                        ...(summary.earnings || {}),
                        grossEarnings: Number(responsePayload.newGross ?? summary?.earnings?.grossEarnings ?? 0),
                    },
                    deductions: {
                        ...(summary.deductions || {}),
                        totalDeductions: Number(
                            responsePayload.newDeductions ?? summary?.deductions?.totalDeductions ?? 0
                        ),
                    },
                }
            })

            const existingTotals = prev.totals || {}
            const updatedRunTotals = responsePayload.updatedRunTotals || {}
            const nextTotals = {
                ...existingTotals,
                ...updatedRunTotals,
            }

            if (typeof responsePayload.pendingAttendanceCount === 'number') {
                nextTotals.pendingAttendanceCount = Math.max(0, Number(responsePayload.pendingAttendanceCount))
            }

            return {
                ...prev,
                totals: nextTotals,
                staffSummaries: updatedSummaries,
                lastUpdatedOn: responsePayload.recomputedOn || prev.lastUpdatedOn,
            }
        })
    }, [])

    const recomputeStaff = useCallback(
        async (summary, acknowledgeWarnings = false) => {
            if (!summary?.staffIdentifier) return
            if (!authCtx?.token || !activeStore?.storeIdentifier) return

            try {
                setRecomputingStaffIdentifier(summary.staffIdentifier)

                const response = await fetch(BASE_URL_API + 'recomputeStaffPayroll', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier: activeStore.storeIdentifier,
                        runIdentifier,
                        staffIdentifier: summary.staffIdentifier,
                        acknowledgeWarnings,
                    }),
                })

                const respo = await response.json()

                if (respo?.status === 'success') {
                    const payload = respo.response || {}
                    applyStaffRecomputeDelta(payload)
                    setStaffAckContext(null)
                    setStaffAckChecked(false)

                    setStaffRecomputeDiffMap((prev) => ({
                        ...prev,
                        [summary.staffIdentifier]: {
                            previousNetPay: Number(payload.previousNetPay || 0),
                            newNetPay: Number(payload.newNetPay || 0),
                            netPayDelta: Number(payload.netPayDelta || 0),
                        },
                    }))

                    const direction = Number(payload.netPayDelta || 0) >= 0 ? '▲' : '▼'
                    toast.success(
                        `${payload.staffName || summary.staffName || 'Staff'}: ₹${formatINR(
                            payload.previousNetPay
                        )} ${direction} ₹${formatINR(payload.newNetPay)}`
                    )
                    return
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                switch (respo?.errorCode) {
                    case 'PAY_003':
                        if (respo?.response?.requiresAcknowledgement) {
                            setStaffAckContext({
                                summary,
                                staffName: respo?.response?.staffName || summary?.staffName,
                                warnings: respo?.response?.warnings || [],
                            })
                            setStaffAckChecked(false)
                        } else {
                            toast.error(respo?.message || 'This staff member cannot be recomputed in this run.')
                        }
                        break
                    case 'PAY_002':
                        toast.error(
                            `Cannot recompute — run is ${respo?.response?.currentStatus || 'in an invalid state'}.`
                        )
                        break
                    case 'PAY_001':
                    case 'PAY_006':
                    case 'VAL_001':
                    case 'VAL_007':
                    case 'SYS_002':
                    default:
                        toast.error(respo?.message || 'Recompute failed.')
                }
            } catch {
                toast.error('Network error.')
            } finally {
                setRecomputingStaffIdentifier('')
            }
        },
        [activeStore?.storeIdentifier, applyStaffRecomputeDelta, authCtx, runIdentifier]
    )

    const handleOverrideStaff = (summary) => {
        if (!summary?.summaryIdentifier) {
            toast.info('Override is not available for this staff row yet.')
            return
        }
        navigate(`/payroll/run/${runIdentifier}/override/${summary.summaryIdentifier}`)
    }

    if (!runIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.inlineAlertWarn}>
                    <AlertTriangle size={16} />
                    <span>Run identifier is missing. Please open a payroll run from history or generator.</span>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button className={styles.backBtn} type="button" onClick={() => navigate(-1)}>
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <button
                        type="button"
                        className={`${styles.heroPill} ${styles.heroPillBtn}`}
                        onClick={() => navigate('/payroll/run-history')}
                    >
                        <CalendarClock size={12} />
                        Payroll History
                    </button>
                    <h1 className={styles.headerTitle}>{runData?.cycleLabel || 'Payroll Run'}</h1>
                    <p className={styles.headerSub}>
                        {runData?.cycleStartDate && runData?.cycleEndDate
                            ? `${runData.cycleStartDate} to ${runData.cycleEndDate}`
                            : 'Loading cycle timeline...'}
                    </p>
                </div>
                <span className={styles.statusPill} style={{ color: statusMeta.color, backgroundColor: statusMeta.bg }}>
                    {statusMeta.label}
                </span>
            </div>

            {isLoading ? (
                <div className={styles.loaderCard}>
                    <LoaderCircle size={20} className={styles.spinIcon} />
                    <span>Loading payroll run details...</span>
                </div>
            ) : !runData ? (
                <div className={styles.inlineAlertWarn}>
                    <CircleAlert size={16} />
                    <span>Unable to load payroll run data.</span>
                </div>
            ) : (
                <>
                    <div className={styles.tabRow}>
                        {[TAB_KEYS.SUMMARY, TAB_KEYS.STAFF, TAB_KEYS.EXCLUDED, TAB_KEYS.WARNINGS].map((tabKey) => {
                            const isActive = activeTab === tabKey
                            const count = tabCounts[tabKey]
                            return (
                                <button
                                    key={tabKey}
                                    type="button"
                                    className={`${styles.tabBtn} ${isActive ? styles.tabBtnActive : ''}`}
                                    onClick={() => setActiveTab(tabKey)}
                                >
                                    <span>{tabKey}</span>
                                    {count !== null ? <span className={styles.tabCount}>{count}</span> : null}
                                </button>
                            )
                        })}
                    </div>

                    {activeTab === TAB_KEYS.SUMMARY ? (
                        <>
                            <section className={styles.metricGrid}>
                                <div className={styles.metricCard}>
                                    <BadgeIndianRupee size={16} />
                                    <strong>₹{formatINR(totals?.totalGrossEarnings)}</strong>
                                    <span>Total Gross</span>
                                </div>
                                <div className={styles.metricCard}>
                                    <CircleDollarSign size={16} />
                                    <strong>₹{formatINR(totals?.totalDeductions)}</strong>
                                    <span>Total Deductions</span>
                                </div>
                                <div className={styles.metricCard}>
                                    <WalletCards size={16} />
                                    <strong>₹{formatINR(totals?.totalNetPayout)}</strong>
                                    <span>Net Payout</span>
                                </div>
                                <div className={styles.metricCard}>
                                    <ShieldAlert size={16} />
                                    <strong>₹{formatINR(totals?.totalEmployerPf)}</strong>
                                    <span>Employer PF Cost</span>
                                </div>
                                <div className={styles.metricCard}>
                                    <Users size={16} />
                                    <strong>{Number(totals?.totalStaffCount || 0)}</strong>
                                    <span>Included Staff</span>
                                </div>
                                <div className={styles.metricCard}>
                                    <FileWarning size={16} />
                                    <strong>{Number(totals?.pendingAttendanceCount || 0)}</strong>
                                    <span>Pending Attendance</span>
                                </div>
                            </section>

                            {warningCount > 0 ? (
                                <section className={styles.panelCard}>
                                    <div className={styles.panelHead}>
                                        <h3>Warnings</h3>
                                        <button
                                            type="button"
                                            className={styles.inlineActionBtn}
                                            onClick={() => setActiveTab(TAB_KEYS.WARNINGS)}
                                        >
                                            View Details <ExternalLink size={13} />
                                        </button>
                                    </div>
                                    <p className={styles.panelHint}>{warningCount} warning(s) found for this run.</p>
                                    <div className={styles.listWrap}>
                                        {warnings.slice(0, 3).map((warning, index) => (
                                            <div key={`${warning?.staffIdentifier || index}-${warning?.type}`} className={styles.warningRow}>
                                                <div>
                                                    <strong>{warning?.staffName || 'Staff'}</strong>
                                                    <span>{warning?.message}</span>
                                                </div>
                                                <span className={styles.typeBadge}>{warning?.type || 'WARNING'}</span>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ) : null}

                            {!canFinalize ? (
                                <section className={styles.panelCard}>
                                    <div className={styles.panelHead}>
                                        <h3>Finalization Gates</h3>
                                    </div>
                                    <div className={styles.listWrap}>
                                        {finalizationGates.map((gate, index) => (
                                            <div key={`${gate}-${index}`} className={styles.gateRow}>
                                                <CircleAlert size={14} />
                                                <span>{gate}</span>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ) : null}

                            {requiresAck ? (
                                <section className={styles.warningAckCard}>
                                    <div className={styles.ackHead}>
                                        <AlertTriangle size={18} />
                                        <span>Review before recomputing:</span>
                                    </div>
                                    <div className={styles.ackWarnings}>
                                        {ackWarnings.map((warning, index) => (
                                            <p key={`${warning?.type || index}`} className={styles.ackWarningItem}>
                                                • {warning?.message || 'Acknowledge this warning.'}
                                            </p>
                                        ))}
                                    </div>
                                    <div className={styles.ackCheckWrapper}>
                                        <input
                                            type="checkbox"
                                            id="ackCheck"
                                            checked={ackCheckboxChecked}
                                            onChange={(e) => setAckCheckboxChecked(e.target.checked)}
                                            className={styles.ackCheckbox}
                                        />
                                        <label htmlFor="ackCheck">I understand — recompute anyway</label>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.primaryBtn}
                                        onClick={() => recomputePayroll(true)}
                                        disabled={!ackCheckboxChecked || isRecomputing}
                                    >
                                        {isRecomputing ? (
                                            <>
                                                <LoaderCircle size={14} className={styles.spinIcon} /> Recomputing...
                                            </>
                                        ) : (
                                            <>
                                                Recompute <ArrowRight size={14} />
                                            </>
                                        )}
                                    </button>
                                </section>
                            ) : null}

                            {diff && diff.changedCount > 0 ? (
                                <section className={styles.diffCard}>
                                    <div className={styles.diffHead}>
                                        <strong>{diff.changedCount} staff value(s) changed:</strong>
                                    </div>
                                    <div className={styles.diffList}>
                                        {diff.changedStaff?.map((staff) => (
                                            <DiffRow key={staff.staffIdentifier} staff={staff} />
                                        ))}
                                    </div>
                                </section>
                            ) : null}

                            <section className={styles.actionCard}>
                                {runStatusKey === 'DRAFT' ? (
                                    <>
                                        <button
                                            type="button"
                                            className={styles.secondaryBtn}
                                            onClick={() => recomputePayroll(false)}
                                            disabled={isRecomputing || requiresAck || isTransitioning}
                                        >
                                            {isRecomputing ? (
                                                <>
                                                    <LoaderCircle size={14} className={styles.spinIcon} /> Recomputing...
                                                </>
                                            ) : (
                                                <>
                                                    <RefreshCw size={14} /> Recompute
                                                </>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.primaryBtn}
                                            onClick={handleMoveToReview}
                                            disabled={isTransitioning}
                                        >
                                            {isTransitioning ? (
                                                <>
                                                    <LoaderCircle size={14} className={styles.spinIcon} /> Moving...
                                                </>
                                            ) : (
                                                <>
                                                    Move to Review <ArrowRight size={14} />
                                                </>
                                            )}
                                        </button>
                                        <div className={styles.exportMenuWrap} ref={exportMenuRef}>
                                            <button
                                                type="button"
                                                className={styles.secondaryBtn}
                                                onClick={() => setIsExportMenuOpen((prev) => !prev)}
                                                disabled={isExporting}
                                            >
                                                {isExporting ? (
                                                    <>
                                                        <LoaderCircle size={14} className={styles.spinIcon} /> Exporting...
                                                    </>
                                                ) : (
                                                    <>
                                                        {exportButtonLabel} <Download size={14} /> <ChevronDown size={13} />
                                                    </>
                                                )}
                                            </button>

                                            {isExportMenuOpen ? (
                                                <div className={styles.exportMenuPanel}>
                                                    {EXPORT_OPTIONS.map((option) => {
                                                        const OptionIcon = option.icon
                                                        return (
                                                            <button
                                                                key={option.format}
                                                                type="button"
                                                                className={styles.exportOptionBtn}
                                                                onClick={() => handleExport(option.format)}
                                                                disabled={isExporting}
                                                            >
                                                                <OptionIcon size={14} />
                                                                <span>
                                                                    <strong>{option.label}</strong>
                                                                    <small>{option.desc}</small>
                                                                </span>
                                                            </button>
                                                        )
                                                    })}
                                                </div>
                                            ) : null}
                                        </div>
                                    </>
                                ) : null}

                                {runStatusKey === 'REVIEW' ? (
                                    <>
                                        <button
                                            type="button"
                                            className={styles.secondaryBtn}
                                            onClick={handleMoveToDraft}
                                            disabled={isTransitioning}
                                        >
                                            {isTransitioning ? (
                                                <>
                                                    <LoaderCircle size={14} className={styles.spinIcon} /> Updating...
                                                </>
                                            ) : (
                                                <>
                                                    Back to Draft
                                                </>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.primaryBtn} ${styles.finalizeBtn}`}
                                            onClick={requestFinalizationToken}
                                            disabled={!canFinalize || isTransitioning}
                                        >
                                            {isTransitioning ? (
                                                <>
                                                    <LoaderCircle size={14} className={styles.spinIcon} /> Preparing...
                                                </>
                                            ) : (
                                                <>
                                                    Finalize Payroll <CheckCircle2 size={14} />
                                                </>
                                            )}
                                        </button>
                                        <div className={styles.exportMenuWrap} ref={exportMenuRef}>
                                            <button
                                                type="button"
                                                className={styles.secondaryBtn}
                                                onClick={() => setIsExportMenuOpen((prev) => !prev)}
                                                disabled={isExporting}
                                            >
                                                {isExporting ? (
                                                    <>
                                                        <LoaderCircle size={14} className={styles.spinIcon} /> Exporting...
                                                    </>
                                                ) : (
                                                    <>
                                                        {exportButtonLabel} <Download size={14} /> <ChevronDown size={13} />
                                                    </>
                                                )}
                                            </button>

                                            {isExportMenuOpen ? (
                                                <div className={styles.exportMenuPanel}>
                                                    {EXPORT_OPTIONS.map((option) => {
                                                        const OptionIcon = option.icon
                                                        return (
                                                            <button
                                                                key={option.format}
                                                                type="button"
                                                                className={styles.exportOptionBtn}
                                                                onClick={() => handleExport(option.format)}
                                                                disabled={isExporting}
                                                            >
                                                                <OptionIcon size={14} />
                                                                <span>
                                                                    <strong>{option.label}</strong>
                                                                    <small>{option.desc}</small>
                                                                </span>
                                                            </button>
                                                        )
                                                    })}
                                                </div>
                                            ) : null}
                                        </div>
                                    </>
                                ) : null}

                                {runStatusKey === 'FINALIZED' ? (
                                    <>
                                        <button
                                            type="button"
                                            className={styles.primaryBtn}
                                            onClick={handleSendPayslips}
                                            disabled={isSendingPayslips}
                                        >
                                            {isSendingPayslips ? (
                                                <>
                                                    <LoaderCircle size={14} className={styles.spinIcon} /> Sending Payslips...
                                                </>
                                            ) : (
                                                <>
                                                    Send All Payslips on WhatsApp <Send size={14} />
                                                </>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.secondaryBtn}
                                            onClick={() => navigate(`/payroll/run/${runIdentifier}/delivery-status`)}
                                        >
                                            <ExternalLink size={14} /> Delivery Status
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.secondaryBtn}
                                            onClick={handleGenerateAllShareLinks}
                                            disabled={isGeneratingShareLinks}
                                        >
                                            {isGeneratingShareLinks ? (
                                                <>
                                                    <LoaderCircle size={14} className={styles.spinIcon} /> Generating Links...
                                                </>
                                            ) : (
                                                <>
                                                    <Share2 size={14} /> Generate All Share Links
                                                </>
                                            )}
                                        </button>
                                        <div className={styles.exportMenuWrap} ref={exportMenuRef}>
                                            <button
                                                type="button"
                                                className={styles.secondaryBtn}
                                                onClick={() => setIsExportMenuOpen((prev) => !prev)}
                                                disabled={isExporting}
                                            >
                                                {isExporting ? (
                                                    <>
                                                        <LoaderCircle size={14} className={styles.spinIcon} /> Exporting...
                                                    </>
                                                ) : (
                                                    <>
                                                        {exportButtonLabel} <Download size={14} /> <ChevronDown size={13} />
                                                    </>
                                                )}
                                            </button>

                                            {isExportMenuOpen ? (
                                                <div className={styles.exportMenuPanel}>
                                                    {EXPORT_OPTIONS.map((option) => {
                                                        const OptionIcon = option.icon
                                                        return (
                                                            <button
                                                                key={option.format}
                                                                type="button"
                                                                className={styles.exportOptionBtn}
                                                                onClick={() => handleExport(option.format)}
                                                                disabled={isExporting}
                                                            >
                                                                <OptionIcon size={14} />
                                                                <span>
                                                                    <strong>{option.label}</strong>
                                                                    <small>{option.desc}</small>
                                                                </span>
                                                            </button>
                                                        )
                                                    })}
                                                </div>
                                            ) : null}
                                        </div>
                                    </>
                                ) : null}
                            </section>

                            <section className={styles.metaCard}>
                                <p>
                                    <strong>Run ID:</strong> {runData?.runIdentifier}
                                </p>
                                <p>
                                    <strong>Generated On:</strong> {formatDateTime(runData?.generatedOn)}
                                </p>
                                <p>
                                    <strong>Last Updated:</strong> {formatDateTime(runData?.lastUpdatedOn)}
                                </p>
                                <p>
                                    <strong>Finalized On:</strong> {formatDateTime(runData?.finalizedOn)}
                                </p>
                            </section>
                        </>
                    ) : null}

                    {activeTab === TAB_KEYS.STAFF ? (
                        <section className={styles.listPanel}>
                            {staffSummaries.length === 0 ? (
                                <div className={styles.emptyState}>No included staff rows in this run.</div>
                            ) : (
                                <div className={styles.listWrap}>
                                    {staffSummaries.map((summary) => (
                                        <div key={summary.summaryIdentifier || summary.staffIdentifier} className={styles.staffRow}>
                                            <div className={styles.staffHead}>
                                                <div className={styles.identityBlock}>
                                                    <FaCircleUser className={styles.avatarIcon} />
                                                    <div>
                                                        <strong>{summary.staffName || 'Staff'}</strong>
                                                        <span>{summary.designationLabel || '—'}</span>
                                                    </div>
                                                </div>
                                                {summary.isBelowMinimumWage ? (
                                                    <span className={styles.minWageBadge}>Below Minimum Wage</span>
                                                ) : null}
                                            </div>

                                            <div className={styles.infoGrid}>
                                                <span>
                                                    Present: {Number(summary?.attendance?.presentDays || 0).toFixed(1)}d
                                                </span>
                                                <span>
                                                    LWP: {Number(summary?.attendance?.lwpDays || 0).toFixed(1)}d
                                                </span>
                                                <span>Late: {Number(summary?.attendance?.lateCount || 0)}</span>
                                            </div>

                                            <div className={styles.compGrid}>
                                                <span>Gross: ₹{formatINR(summary?.earnings?.grossEarnings)}</span>
                                                <span>Deductions: ₹{formatINR(summary?.deductions?.totalDeductions)}</span>
                                                <span>Net: ₹{formatINR(summary?.netPay)}</span>
                                            </div>

                                            <div className={styles.rowFoot}>
                                                <span>
                                                    Service Revenue: ₹{formatINR(summary?.earnings?.totalServiceRevenue)}
                                                </span>
                                            </div>

                                            <div className={styles.staffActionRow}>
                                                <button
                                                    type="button"
                                                    className={styles.inlineActionBtn}
                                                    onClick={() => handleStaffRowPress(summary)}
                                                >
                                                    View Payslip <FaArrowRightLong size={12} />
                                                </button>
                                                {runStatusKey === 'DRAFT' ? (
                                                    <button
                                                        type="button"
                                                        className={styles.inlineActionBtn}
                                                        onClick={() => handleOverrideStaff(summary)}
                                                    >
                                                        Override <PencilLine size={12} />
                                                    </button>
                                                ) : null}
                                                {runStatusKey === 'DRAFT' ? (
                                                    <button
                                                        type="button"
                                                        className={styles.inlineActionBtn}
                                                        onClick={() => recomputeStaff(summary, false)}
                                                        disabled={
                                                            recomputingStaffIdentifier === summary.staffIdentifier ||
                                                            Boolean(staffAckContext)
                                                        }
                                                    >
                                                        {recomputingStaffIdentifier === summary.staffIdentifier ? (
                                                            <>
                                                                <LoaderCircle size={12} className={styles.spinIcon} /> Recomputing...
                                                            </>
                                                        ) : (
                                                            <>
                                                                Recompute <RefreshCw size={12} />
                                                            </>
                                                        )}
                                                    </button>
                                                ) : null}
                                            </div>

                                            {staffAckContext?.summary?.staffIdentifier === summary?.staffIdentifier ? (
                                                <div className={styles.staffAckCard}>
                                                    <div className={styles.staffAckHead}>
                                                        <AlertTriangle size={14} />
                                                        <strong>
                                                            {staffAckContext?.staffName || summary?.staffName}: recompute warning
                                                        </strong>
                                                    </div>
                                                    <div className={styles.staffAckWarnings}>
                                                        {(staffAckContext?.warnings || []).map((warning, index) => (
                                                            <p key={`${warning?.type || index}-${summary?.staffIdentifier}`}>
                                                                • {warning?.message || 'Warning received from server.'}
                                                            </p>
                                                        ))}
                                                    </div>
                                                    <label className={styles.staffAckToggle}>
                                                        <input
                                                            type="checkbox"
                                                            checked={staffAckChecked}
                                                            onChange={(event) => setStaffAckChecked(event.target.checked)}
                                                        />
                                                        <span>I understand — recompute anyway</span>
                                                    </label>
                                                    <button
                                                        type="button"
                                                        className={styles.primaryBtn}
                                                        onClick={() => recomputeStaff(summary, true)}
                                                        disabled={!staffAckChecked || recomputingStaffIdentifier === summary.staffIdentifier}
                                                    >
                                                        Confirm Recompute <ArrowRight size={12} />
                                                    </button>
                                                </div>
                                            ) : null}

                                            {staffRecomputeDiffMap?.[summary.staffIdentifier] ? (
                                                <StaffRecomputeDelta diff={staffRecomputeDiffMap[summary.staffIdentifier]} />
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    ) : null}

                    {activeTab === TAB_KEYS.EXCLUDED ? (
                        <section className={styles.listPanel}>
                            {excludedStaff.length === 0 ? (
                                <div className={styles.emptyState}>No excluded staff for this run.</div>
                            ) : (
                                <div className={styles.listWrap}>
                                    {excludedStaff.map((staff) => {
                                        const exclusionMeta = EXCLUSION_STATUS_CONFIG[staff?.inclusionStatus] || {
                                            label: staff?.inclusionStatus || 'Excluded',
                                            color: '#475569',
                                            bg: '#E2E8F0',
                                        }

                                        return (
                                            <div key={staff.staffIdentifier} className={styles.excludedRow}>
                                                <div className={styles.staffHead}>
                                                    <div className={styles.identityBlock}>
                                                        <FaCircleUser className={styles.avatarIcon} />
                                                        <div>
                                                            <strong>{staff.staffName || 'Staff'}</strong>
                                                            <span>{staff.designationLabel || '—'}</span>
                                                        </div>
                                                    </div>
                                                    <span
                                                        className={styles.exclusionStatusBadge}
                                                        style={{ color: exclusionMeta.color, backgroundColor: exclusionMeta.bg }}
                                                    >
                                                        {exclusionMeta.label}
                                                    </span>
                                                </div>
                                                <p>{staff.note || 'No note provided.'}</p>
                                                <div className={styles.staffActionRow}>
                                                    <button
                                                        type="button"
                                                        className={styles.inlineActionBtn}
                                                        onClick={() =>
                                                            navigate(`/payroll/fnf/${staff.staffIdentifier}`, {
                                                                state: {
                                                                    staffIdentifier: staff.staffIdentifier,
                                                                    staffName: staff.staffName,
                                                                    runIdentifier,
                                                                },
                                                            })
                                                        }
                                                    >
                                                        Generate FnF <ExternalLink size={13} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.inlineActionBtn}
                                                        onClick={() =>
                                                            navigate(`/payroll/fnf/${staff.staffIdentifier}`, {
                                                                state: {
                                                                    staffIdentifier: staff.staffIdentifier,
                                                                    staffName: staff.staffName,
                                                                    runIdentifier,
                                                                    activeTab: 'HISTORY',
                                                                },
                                                            })
                                                        }
                                                    >
                                                        FnF History <ExternalLink size={13} />
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </section>
                    ) : null}

                    {activeTab === TAB_KEYS.WARNINGS ? (
                        <section className={styles.listPanel}>
                            {warnings.length === 0 ? (
                                <div className={styles.emptyState}>No warnings recorded in this run.</div>
                            ) : (
                                <div className={styles.listWrap}>
                                    {warnings.map((warning, index) => (
                                        <div
                                            key={`${warning?.staffIdentifier || index}-${warning?.type || 'warn'}`}
                                            className={styles.warningDetailRow}
                                        >
                                            <div className={styles.warningHeadRow}>
                                                <strong>{warning?.staffName || 'Staff'}</strong>
                                                <span className={styles.typeBadge}>{warning?.type || 'WARNING'}</span>
                                            </div>
                                            <p>{warning?.message || 'No warning message provided.'}</p>

                                            {String(warning?.type || '').toUpperCase() === 'UNACCEPTED_ATTENDANCE' ? (
                                                <button
                                                    type="button"
                                                    className={styles.inlineActionBtn}
                                                    onClick={() => openAttendanceScreen(warning)}
                                                >
                                                    Open Attendance Screen <ExternalLink size={13} />
                                                </button>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    ) : null}
                </>
            )}

            {showFinalizeModal ? (
                <div className={styles.finalizeModalBackdrop} role="presentation" onClick={() => !isTransitioning && resetFinalizationModal()}>
                    <section
                        className={styles.finalizeModalCard}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Finalize Payroll Confirmation"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className={styles.finalizeModalHead}>
                            <div>
                                <h3>Finalize {runData?.cycleLabel || finalizationSummary?.cycleLabel || 'Payroll'}?</h3>
                                <p>{finalizationMessage || 'This action cannot be undone once finalized.'}</p>
                            </div>
                            <button
                                type="button"
                                className={styles.modalCloseBtn}
                                onClick={resetFinalizationModal}
                                disabled={isTransitioning}
                            >
                                <X size={15} />
                            </button>
                        </div>

                        <div className={styles.finalizeSummaryGrid}>
                            <div className={styles.finalizeSummaryItem}>
                                <span>Staff Members</span>
                                <strong>{Number(finalizationSummary?.totalStaff || totals?.totalStaffCount || 0)}</strong>
                            </div>
                            <div className={styles.finalizeSummaryItem}>
                                <span>Gross Earnings</span>
                                <strong>₹{formatINR(finalizationSummary?.totalGrossEarnings || totals?.totalGrossEarnings)}</strong>
                            </div>
                            <div className={styles.finalizeSummaryItem}>
                                <span>Total Deductions</span>
                                <strong>₹{formatINR(finalizationSummary?.totalDeductions || totals?.totalDeductions)}</strong>
                            </div>
                            <div className={styles.finalizeSummaryItem}>
                                <span>Net Payout</span>
                                <strong>₹{formatINR(finalizationSummary?.totalNetPayout || totals?.totalNetPayout)}</strong>
                            </div>
                        </div>

                        <div className={styles.finalizeWarningBlock}>
                            <div className={styles.finalizeWarningHead}>
                                <AlertTriangle size={15} />
                                <strong>This action cannot be undone.</strong>
                            </div>
                            <ul className={styles.finalizeWarningList}>
                                <li>Leave balances will be updated.</li>
                                <li>Advance EMIs will be marked as deducted.</li>
                                <li>Payslips will be locked for this run.</li>
                            </ul>
                            {confirmationTokenExpiresIn > 0 ? (
                                <p className={styles.finalizeTokenHint}>
                                    Confirmation expires in ~{Math.ceil(confirmationTokenExpiresIn / 60)} min.
                                </p>
                            ) : null}
                        </div>

                        <div className={styles.finalizeModalActions}>
                            <button
                                type="button"
                                className={styles.secondaryBtn}
                                onClick={resetFinalizationModal}
                                disabled={isTransitioning}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={`${styles.primaryBtn} ${styles.destructiveBtn}`}
                                onClick={confirmFinalization}
                                disabled={isTransitioning}
                            >
                                {isTransitioning ? (
                                    <>
                                        <LoaderCircle size={14} className={styles.spinIcon} /> Finalizing...
                                    </>
                                ) : (
                                    <>
                                        Finalize & Lock <ArrowRight size={14} />
                                    </>
                                )}
                            </button>
                        </div>
                    </section>
                </div>
            ) : null}
        </div>
    )
}

export default PayrollRunDetailScreen

const DiffRow = ({ staff }) => {
    if (!staff) return null

    const increased = Number(staff.netPayDelta || 0) > 0
    const TrendIcon = increased ? TrendingUp : TrendingDown
    const trendColor = increased ? '#2E7D32' : '#C62828'

    return (
        <div className={styles.diffRow}>
            <div className={styles.diffRowLeft}>
                <strong>{staff.staffName || 'Staff'}</strong>
                <p>Net Pay</p>
            </div>
            <div className={styles.diffRowRight}>
                <span className={styles.diffPrev}>₹{formatINR(staff.previousNetPay)}</span>
                <span className={styles.diffArrow}>→</span>
                <span className={styles.diffNew} style={{ color: trendColor }}>
                    ₹{formatINR(staff.newNetPay)}
                </span>
                <span className={styles.diffDelta} style={{ color: trendColor, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    {increased ? '+' : '−'}₹{formatINR(Math.abs(staff.netPayDelta))}
                    <TrendIcon size={13} />
                </span>
            </div>
        </div>
    )
}

const StaffRecomputeDelta = ({ diff }) => {
    if (!diff) return null

    const increased = Number(diff.netPayDelta || 0) >= 0
    const trendColor = increased ? '#2E7D32' : '#C62828'

    return (
        <div className={styles.staffDeltaRow}>
            <span className={styles.staffDeltaText}>
                ₹{formatINR(diff.previousNetPay)} → ₹{formatINR(diff.newNetPay)}
            </span>
            <span className={styles.staffDeltaValue} style={{ color: trendColor }}>
                {formatSignedINR(diff.netPayDelta)}
            </span>
        </div>
    )
}